import type { FieldValues, UseFormReturn } from "react-hook-form";

import { useRef } from "react";

/**
 * Two-phase submit: RHF validation first, then native submit to useActionState.
 * The form has action={submitFormAction} (Server Action via useActionState). We
 * must run React Hook Form validation before the form actually posts to that
 * action. So we intercept the first submit, run methods.handleSubmit(); only
 * when validation passes do we set allowNativeSubmitRef and requestSubmit() so
 * the second submit is allowed to proceed and the action receives the form.
 */
export function useHandleFormSubmit<TFieldValues extends FieldValues>(
  methods: UseFormReturn<TFieldValues>,
) {
  const allowNativeSubmitRef = useRef(false);

  const handleFormSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    if (allowNativeSubmitRef.current) {
      allowNativeSubmitRef.current = false;
      return;
    }
    e.preventDefault();
    const form = e.currentTarget;
    methods.handleSubmit(() => {
      // Allow the next submit event to proceed (no preventDefault) so the form
      // action receives FormData from the browser. Defer so the current submit
      // and validation complete first; the programmatic submit then runs in the
      // next task and triggers the action with one user click.
      allowNativeSubmitRef.current = true;
      setTimeout(() => form.requestSubmit(), 0);
    })(e);
  };

  return handleFormSubmit;
}
